import { useState, useEffect } from 'react';
import Input from '../Input/Input';
import Tooltip from 'components/Tooltip/Tooltip';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import './NumberStepper.scss';

/**
 * Renders a number stepper input field and optional label wrapped in a form-group div.
 * 
 * Props: autoFocus, inputClassName, formGroupClassName, errorText, disabled, id, isValid, label, name, onChange, required, step, min, max, tooltip, tooltipIcon, tooltipContent,
 * 
 * @example
 * <NumberStepper
 *   formGroupClassName='left-column-on-desktop'
 *   id="water"
 *   label="Water:"
 *   min={0}
 *   step='any'
 *   value={isNaN(water) ? '' : water}
 *   onChange={(e) => setWater(e.target.value)}
 *   tooltip
 *   tooltipContent={
 *     <>
 *       We're counting by red cups<br /><br />
 *       5 red cups = 1 gallon<br /><br />
 *       1 red cup = about 26oz<br /><br />
 *       128oz = 1 gallon
 *     </>
 *   }
 * />
 */
const NumberStepper = (props) => {
  const [incrementDisabled, setIncrementDisabled] = useState(false);
  const [decrementDisabled, setDecrementDisabled] = useState(false);
  const step = isNaN(props.step) ? 1 : props.step ?? 1;

  useEffect(() => {
    const val = parseFloat(props.value) || 0;
    setIncrementDisabled(props.max !== undefined && val >= props.max);
    setDecrementDisabled(props.min !== undefined && val <= props.min);
  }, [props.value, props.min, props.max]);

  const scale = 10 ** (step.toString().split('.')[1]?.length || 0);

  const roundToStep = (num) => {
    return Math.round(num * scale) / scale;
  };

  const increment = () => {
    const val = parseFloat(props.value) || 0;
    const newValue = Number(roundToStep((val * scale + (step * scale)) / scale));
    if (props.value === null) {
      props.onChange({ target: { value: 0 } });
    } else if (props.max !== undefined && newValue >= props.max) {
      props.onChange({ target: { value: props.max } });
    } else {
      props.onChange({ target: { value: newValue } });
    }
  };

  const decrement = () => {
    const val = parseFloat(props.value) || 0;
    const newValue = roundToStep((val * scale - (step * scale)) / scale);
    if (props.min !== undefined && newValue <= props.min) {
      props.onChange({ target: { value: props.min } });
    } else {
      props.onChange({ target: { value: newValue } });
    }
  };

  let numberStepperClassName = `number-stepper`;
  if (props.inputClassName) {
    numberStepperClassName += ` ${props.inputClassName}`;
  }

  let formGroupClassName = `form-group`;
  if (props.formGroupClassName) {
    formGroupClassName += ` ${props.formGroupClassName}`;
  }
  if (props.isValid === false) {
    formGroupClassName += ` form-group--invalid`;
  }

  let incrementButtonClassName = 'number-stepper__increment-button';
  if (incrementDisabled) {
    incrementButtonClassName += ' number-stepper__increment-button--disabled';
  }

  let decrementButtonClassName = 'number-stepper__decrement-button';
  if (decrementDisabled) {
    decrementButtonClassName += ' number-stepper__decrement-button--disabled';
  }

  if (props.tooltip) {
    formGroupClassName += ` form-group--number-stepper-with-tooltip`;
  }

  return (
    <div className={formGroupClassName}>
      {props.label && <label className='label input__label' htmlFor={props.id}>{props.label}</label>}
      <div className={numberStepperClassName} disabled={props.disabled}>
        <RemoveIcon
          className={decrementButtonClassName}
          onClick={() => !decrementDisabled && decrement()}
        />
        <Input
          className='input'
          autoFocus={props.autoFocus}
          type='number'
          min={props.min}
          max={props.max}
          step={props.step}
          disabled={props.disabled}
          id={props.id}
          name={props.name}
          onChange={props.onChange}
          required={props.required}
          value={props.value ?? ''}
          isValid={props.isValid}
        />
        <AddIcon
          className={incrementButtonClassName}
          onClick={() => !incrementDisabled && increment()}
        />
        {props.errorText && <span className='error-text input__error-text'>{props.errorText}</span>}
      </div>
      {props.tooltip &&
        <Tooltip
          className={props.label ? 'tooltip--in-form-group-with-label' : ''}
          icon={props.tooltipIcon}
          content={props.tooltipContent}
        />
      }
    </div>
  );
};

export default NumberStepper;
